import React, { useState } from 'react';
import { motion } from 'framer-motion';

const AlakartConfig = ({ onSave, isDarkMode }) => {
    const [portion, setPortion] = useState(4);
    const [selectedMeals, setSelectedMeals] = useState({
        BREAKFAST: true,
        SOUP: true,
        MAIN: true,
        SALAD: true,
        DESSERT: true
    });

    const mealOptions = [
        { id: 'BREAKFAST', label: 'Kahvaltı', icon: 'breakfast_dining', color: 'from-amber-400 to-orange-500' },
        { id: 'SOUP', label: 'Çorba', icon: 'soup_kitchen', color: 'from-orange-500 to-red-500' },
        { id: 'MAIN', label: 'Ana Yemek', icon: 'restaurant_menu', color: 'from-red-500 to-pink-500' },
        { id: 'SALAD', label: 'Salata', icon: 'eco', color: 'from-green-500 to-emerald-500' },
        { id: 'DESSERT', label: 'Tatlı', icon: 'icecream', color: 'from-purple-500 to-pink-500' }
    ];

    const handleToggleMeal = (mealId) => {
        setSelectedMeals(prev => ({
            ...prev,
            [mealId]: !prev[mealId]
        }));
    };

    const handleSave = () => {
        const activeMeals = Object.keys(selectedMeals).filter(key => selectedMeals[key]);
        if (activeMeals.length === 0) {
            alert('En az bir öğün seçmelisiniz!');
            return;
        }
        onSave({ portion, meals: activeMeals });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-6">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 md:p-12 border border-slate-200 dark:border-slate-700"
            >
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <span className="material-symbols-outlined text-4xl text-primary">restaurant</span>
                        <span className="material-symbols-outlined text-4xl text-amber-500">calendar_month</span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white mb-2">
                        Haftalık Planınızı <span className="text-primary italic">Oluşturun</span>
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base">
                        Öğünlerinizi seçin ve planlamaya başlayın
                    </p>
                </div>

                {/* Portion Selection */}
                <div className="mb-10">
                    <label className="block text-sm font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-4">
                        <span className="material-symbols-outlined text-lg align-middle mr-2">group</span>
                        Kaç Kişilik Yemek Pişiriyorsunuz?
                    </label>
                    <div className="flex items-center justify-center gap-6 bg-slate-100 dark:bg-slate-900 rounded-2xl p-6">
                        <button
                            onClick={() => setPortion(Math.max(1, portion - 1))}
                            className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 shadow-md hover:shadow-lg transition-all flex items-center justify-center text-slate-600 dark:text-slate-300 hover:text-primary"
                        >
                            <span className="material-symbols-outlined text-2xl">remove</span>
                        </button>
                        <div className="text-center min-w-[100px]">
                            <div className="text-5xl font-black text-primary">{portion}</div>
                            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-1">Kişilik</div>
                        </div>
                        <button
                            onClick={() => setPortion(Math.min(12, portion + 1))}
                            className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 shadow-md hover:shadow-lg transition-all flex items-center justify-center text-slate-600 dark:text-slate-300 hover:text-primary"
                        >
                            <span className="material-symbols-outlined text-2xl">add</span>
                        </button>
                    </div>
                </div>

                {/* Meal Selection */}
                <div className="mb-10">
                    <label className="block text-sm font-black text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-4">
                        <span className="material-symbols-outlined text-lg align-middle mr-2">restaurant_menu</span>
                        Hangi Öğünleri Planlamak İstiyorsunuz?
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        {mealOptions.map(meal => {
                            const isSelected = selectedMeals[meal.id];
                            return (
                                <motion.button
                                    key={meal.id}
                                    onClick={() => handleToggleMeal(meal.id)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`relative p-6 rounded-2xl border-2 transition-all ${isSelected
                                        ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-lg'
                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600'
                                        }`}
                                >
                                    <div className="flex flex-col items-center gap-3">
                                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${meal.color} flex items-center justify-center shadow-lg`}>
                                            <span className="material-symbols-outlined text-3xl text-white">{meal.icon}</span>
                                        </div>
                                        <span className={`font-black text-sm ${isSelected ? 'text-primary' : 'text-slate-600 dark:text-slate-400'}`}>
                                            {meal.label}
                                        </span>
                                    </div>
                                    {isSelected && (
                                        <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                            <span className="material-symbols-outlined text-white text-sm">check</span>
                                        </div>
                                    )}
                                </motion.button>
                            );
                        })}
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 text-center italic">
                        En az bir öğün seçmelisiniz
                    </p>
                </div>

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    className="w-full bg-gradient-to-r from-primary to-red-500 text-white font-black py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2 text-lg"
                >
                    <span className="material-symbols-outlined">check_circle</span>
                    Planlamaya Başla
                </button>
            </motion.div>
        </div>
    );
};

export default AlakartConfig;
